import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NoRecords from '~/components/General/NoRecords';
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import {
  DownloadFromTable,
  DownloadFromTableHover,
  NoRecordsDownloadIcon,
  BtnDownloadIcon,
} from '~/assets/images';
import { displayTooltip } from '~/hooks/displayTooltip';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { downloadFile } from '~/utils/helperFunctions';
import { useMediaQuery } from '@mui/material';
import TruncatedText from '~/components/General/TruncatedText';

const MAX_INTRINSIC_ROWS = 7;

const ContactImportTable = ({ pagination, updatePagination, initialLoad }) => {
  const {
    contactImports: rows = [],
    to,
    from,
    lastPage = 1,
    currentPage = 1,
    loading: isLoading,
    rowsCount = 0,
  } = useSelector((state) => state.contactImportsReducers);
  const perPage =
    useSelector((state) => state.contactImportsReducers.perPage) || DEFAULT_PAGE_SIZES[0];
  const isTooltip = displayTooltip();
  const navigate = useNavigate();
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down(1650));
  const showLoader = initialLoad || isLoading || paginationLoading;
  const useIntrinsicTableHeight =
    !showLoader && rows.length > 0 && rows.length <= MAX_INTRINSIC_ROWS;

  const [downloading, setDownloading] = useState({
    status: false,
    logId: null,
  });

  // Reset pagination loading when new data arrives
  React.useEffect(() => {
    setPaginationLoading(false);
  }, [rows]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPaginationLoading(true);
      updatePagination({ page: newPage, pageSize: pagination.pageSize });
    },
    [pagination.pageSize, updatePagination]
  );

  const handleChangePageSize = useCallback(
    (event) => {
      setPaginationLoading(true);
      updatePagination({ page: 1, pageSize: event.target.value });
    },
    [updatePagination]
  );

  const downloadCSV = async (csvFileUrl, csvFileName, logId) => {
    setDownloading({
      status: true,
      logId: logId,
    });

    await downloadFile(csvFileUrl, csvFileName);

    setDownloading({
      status: false,
      logId: null,
    });
  };

  const columns = [
    {
      field: 'fileName',
      headerName: 'File Name',
      flex: 1,
      minWidth: 180,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => {
        const fileName = params.row.fileName || '';
        return (
          <TruncatedText
            text={<span className="fileNameTextTable">{params.row.fileName}</span> || ''}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Import Date',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 105,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => <Typography>{dateFormat(params.row.createdAt, true)}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: isSmallScreen ? 150 : 100,
      compactShrinkMinWidth: 85,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => <Typography>{params.row.status}</Typography>,
    },
    {
      field: 'imported',
      headerName: 'Added',
      flex: 0.6,
      minWidth: 80,
      compactShrinkMinWidth: 80,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => (
        <Typography>{params.row?.inDbDuplicationResult?.imported || 0}</Typography>
      ),
    },
    {
      field: 'duplicate',
      headerName: 'Updated',
      flex: 0.6,
      minWidth: isSmallScreen ? 100 : 80,
      compactShrinkMinWidth: 90,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => (
        <Typography>{params.row?.inDbDuplicationResult?.duplicate || 0}</Typography>
      ),
    },
    {
      field: 'error',
      headerName: 'Failed',
      flex: 0.6,
      minWidth: 80,
      compactShrinkMinWidth: 75,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => (
        <Typography>{params.row?.inFileDuplicationResult?.error || 0}</Typography>
      ),
    },
    {
      field: 'isDnc',
      headerName: 'Do Not Contact',
      flex: 0.8,
      minWidth: 150,
      compactShrinkMinWidth: 90,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => <Typography>{params.row?.isDnc ? 'Yes' : 'No'}</Typography>,
    },
    {
      field: 'creator',
      headerName: 'Imported By',
      flex: 1,
      minWidth: 150,
      compactShrinkMinWidth: 100,
      compactShrinkPriority: 1,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row?.creator?.fullName || ''} />,
    },
    {
      field: 'download',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.fileName} disableHoverListener={isTooltip} disableInteractive>
          <Box
            className="addressWrapper"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '100%',
              marginRight: '28px',
            }}
            onClick={() => downloadCSV(params.row.fileUrl, params.row.fileName, params.row.id)}
          >
            {downloading.status && downloading.logId === params.row?.id ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              <Box className="downloadIconWrapper">
                <img src={DownloadFromTable} alt="Download" className="downloadTableIcon" />
                <img
                  src={DownloadFromTableHover}
                  alt="Download"
                  className="downloadTableIconHover"
                />
              </Box>
            )}
          </Box>
        </Tooltip>
      ),
    },
  ];

  // Add delay for showing no rows overlay
  React.useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && rows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, rows.length]);

  if (rows?.length === 0 && !showLoader) {
    return (
      <Box className="importExportEmptyState">
        <NoRecords
          icon={<img src={NoRecordsDownloadIcon} alt="no-records" />}
          title={MESSAGES?.ACCOUNT?.CONTACT_IMPORT?.NO_RECORDS_TITLE || 'No Imports Yet'}
          message={
            MESSAGES?.ACCOUNT?.CONTACT_IMPORT?.NO_RECORDS_MESSAGE ||
            'Import allow you to bring contacts, properties, or lists into the platform so you can manage and use them in your campaigns.'
          }
          linkText="Learn more"
          linkUrl="https://help.openletterconnect.com/olc-help-docs/getting-started/setting-up-your-account/importing-contacts#step-by-step-importing-contacts"
          buttonText={'Import Contacts'}
          onClickButton={() => navigate('/contacts/import')}
          buttonIcon={<img src={BtnDownloadIcon} alt="import" />}
          minBoxHeight="307px"
        />
      </Box>
    );
  }

  return (
    <Box
      className={`olcTableGridInternal lgTableHorizontalScroll ${initialLoad || isLoading || paginationLoading ? 'table-loading' : ''} ${rows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight={useIntrinsicTableHeight}
          columnHeaderHeight={47}
          getRowId={(row) => row.id}
          disableColumnMenu
          disableRowSelectionOnClick
          pagination={false}
          rowHeight={61}
          shrinkColumnsToFit
          loading={initialLoad || isLoading || paginationLoading}
          sx={{
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && rows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '200px',
                }
              : {}),
          }}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={DEFAULT_PAGE_SIZES}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          from={from}
          to={to}
          total={rowsCount}
          loading={showLoader}
        />
      )}
    </Box>
  );
};

export default ContactImportTable;
